import { getAuditTypeLabel } from "./auditTypes";
import { getStreamNameFor, streamText, toStreamEmails } from "./reportStream";
import {
	collectReportExportEmails,
	getReportExportData,
	withFoundName,
} from "./reportStreamExport";

// The fields of an audit the Audit stream reads.
export type StreamAudit = {
	report_number?: unknown;
	title?: unknown;
	audit_type?: unknown;
	department?: unknown;
	customer?: unknown;
	// An internal audit's is a Terra user's email; an external audit's is
	// typed in.
	auditor?: unknown;
	auditee?: unknown;
	summary?: unknown;
	operator?: string;
	reviewer?: unknown;
	primary_author?: string | null;
	author?: string | null;
	findings?: unknown;
};

// The lead auditors to look up by name.
export const collectAuditorEmails = (audits: readonly StreamAudit[]) =>
	toStreamEmails(audits.map((audit) => audit.auditor));

// The lead auditor as the stream shows them: a known user's name, otherwise
// what was stored. Management Reviews have none.
export function getLeadAuditor(
	audit: StreamAudit,
	emailToName: Record<string, string>
) {
	const stored = streamText(audit.auditor);
	if (stored === "") return "—";
	return getStreamNameFor(emailToName, stored) || stored; // NOSONAR: an empty stored name falls back to what was stored
}

// The line under an audit's title: its type, then its department and, on an
// external audit, its customer, each only when set.
export function getAuditMeta(audit: StreamAudit) {
	const type = streamText(audit.audit_type);
	const customer = streamText(audit.customer);
	return {
		type: type === "" ? undefined : getAuditTypeLabel(type),
		details: [
			streamText(audit.department),
			customer && `Customer: ${customer}`,
		].filter((detail) => detail !== ""),
	};
}

// The auditee as the stream shows it. A Management Review's is the master
// tenant's key, which the edit drawer fills in: shown as that tenant's name
// when it is known.
export function getAuditee(
	audit: StreamAudit,
	getTenantName: (tenantKey: string) => string | undefined
) {
	const stored = streamText(audit.auditee);
	if (stored === "" || audit.audit_type !== "management_review") return stored;
	return getTenantName(stored) || stored; // NOSONAR: an empty tenant name falls back to the key
}

// What the stream's search looks through for an audit.
export const getAuditSearchTexts = (
	audit: StreamAudit,
	emailToName: Record<string, string>,
	getTenantName: (tenantKey: string) => string | undefined
) => [
	audit.report_number,
	audit.title,
	getAuditTypeLabel(audit.audit_type),
	audit.department,
	audit.customer,
	audit.auditor,
	getLeadAuditor(audit, emailToName),
	audit.auditee,
	getAuditee(audit, getTenantName),
	audit.summary,
	audit.operator ? getTenantName(audit.operator) : undefined,
];

// Everyone an audit's PDF names: the lead auditor, the reviewer, the author,
// and each finding's responsible person and, where recorded, its completer.
export const collectAuditExportEmails = (audit: StreamAudit) =>
	toStreamEmails([
		audit.auditor,
		...collectReportExportEmails(audit, "findings"),
	]);

// The audit as its PDF reads it, with the names found, as the audit page
// exports it: a person's name is added only when one is found, as the page's
// lookup does, and each finding gets its people's names.
export const getAuditExportData = <T extends StreamAudit>(
	audit: T,
	emailToName: Record<string, string>
) => ({
	...getReportExportData(audit, emailToName, "findings"),
	...withFoundName("auditor_name", audit.auditor, emailToName),
});
